import { useState, useEffect } from 'preact/hooks';
import { Comment as CommentType } from '@novelizing/types';

interface UserInfo {
  id: number;
  name: string;
  username: string;
}

export const useUserInfo = (comments: CommentType[]) => {
  const [userInfoMap, setUserInfoMap] = useState<Record<number, UserInfo>>({});

  useEffect(() => {
    if (comments.length > 0) {
      const userIds = [
        ...new Set(
          comments
            .map((comment) => {
              // Handle both string and number userId types
              const userId = typeof comment.userId === 'string' 
                ? parseInt(comment.userId, 10) 
                : comment.userId;
              return userId;
            })
            .filter((id) => !isNaN(id))
        ),
      ];

      // Filter out any userIds already in the map to avoid refetching
      const idsToFetch = userIds.filter((id) => !(id in userInfoMap));

      if (idsToFetch.length > 0) {
        const fetchUserInfo = async () => {
          try {
            const response = await fetch('/api/v0/users/get-basic-info', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ userIds: idsToFetch }),
            });
            if (!response.ok) {
              throw new Error('Failed to fetch user info');
            }
            const users: UserInfo[] = await response.json();
            const newUserInfoMap = users.reduce((acc, user) => {
              acc[user.id] = user;
              return acc;
            }, {} as Record<number, UserInfo>);
            // Merge with existing map to avoid re-fetching known users unnecessarily
            // Although in this simple case, it might re-fetch, a more complex hook could cache
            setUserInfoMap((prevMap) => ({ ...prevMap, ...newUserInfoMap }));
          } catch (fetchError) {
            console.error('Error fetching user info:', fetchError);
            // Handle error appropriately, maybe return an error state from the hook
          }
        };

        // Debounce fetchUserInfo call
        const timeoutId = setTimeout(fetchUserInfo, 300);

        // Cleanup function to clear timeout if comments change again quickly
        return () => clearTimeout(timeoutId);
      }
    }
  }, [comments, userInfoMap]);

  return { userInfoMap };
};
